import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    Loader2,
    ArrowLeft,
    Play,
    Pause,
    Turtle,
    Volume2,
    FileText,
    Sparkles,
    Plus,
    Mic,
    StopCircle,
    Headphones,
    Trophy,
    Wand2,
    CheckCircle2,
    XCircle,
    ChevronDown,
    ChevronUp,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { handlePaywallError, formatQuotaLine, isQuotaExhausted } from "@/lib/access";
import { useQuotas } from "@/hooks/use-quotas";
import { useDashboard } from "@/contexts/DashboardContext";

/* -------------------- Podcast vocabulary row (per-word add) --------------
 * The bulk "Tout ajouter" button lives one level up; this row also exposes
 * a Plus button so learners can cherry-pick which words go to /review. */
function PodcastVocabRow({ v, index, podcastId, onAdded, bulkAdded }) {
    const [busy, setBusy] = useState(false);
    const [added, setAdded] = useState(false);
    const isAdded = added || bulkAdded;

    async function addOne() {
        if (busy || isAdded) return;
        setBusy(true);
        try {
            const { data } = await api.post(`/podcast/${podcastId}/add_to_reviews`, { words: [v] });
            if ((data?.added ?? 0) > 0) {
                toast.success(`« ${v.word} » ajouté à vos révisions.`);
            } else {
                toast.info(`« ${v.word} » est déjà dans vos révisions.`);
            }
            setAdded(true);
            onAdded?.();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    }

    return (
        <li
            className="bg-white border border-lexiBorder rounded-xl p-3 text-sm flex items-start justify-between gap-3"
            data-testid={`podcast-vocab-item-${index}`}
        >
            <div className="min-w-0 flex-1">
                <div>
                    <span className="font-serif text-base">{v.word}</span>
                    <span className="text-inkSoft"> — {v.translation_fr}</span>
                    {v.ipa && <span className="text-[11px] text-inkSoft ml-2">{v.ipa}</span>}
                </div>
                {v.natural_sentence && (
                    <div className="text-[11px] text-inkSoft italic mt-1">
                        « {v.natural_sentence} » — {v.sentence_translation_fr}
                    </div>
                )}
            </div>
            <button
                onClick={addOne}
                disabled={busy || isAdded}
                data-testid={`podcast-vocab-add-${index}`}
                title={isAdded ? "Déjà ajouté" : "Ajouter ce mot aux révisions"}
                className={`shrink-0 inline-flex items-center gap-1 text-[11px] rounded-full px-2 py-1 transition-colors ${isAdded
                    ? "bg-emerald/15 text-emerald border border-emerald/30 cursor-default"
                    : "bg-paperAlt hover:bg-terracotta hover:text-white border border-lexiBorder"}`}
            >
                {busy ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                ) : isAdded ? (
                    <CheckCircle2 className="w-3 h-3" />
                ) : (
                    <Plus className="w-3 h-3" />
                )}
                {isAdded ? "Ajouté" : "Ajouter"}
            </button>
        </li>
    );
}

/* --------------------------- LIBRARY VIEW -------------------------------- */

const DURATIONS = [
    { seconds: 60, label: "60 secondes", hint: "un flash rapide" },
    { seconds: 120, label: "2 minutes", hint: "confort quotidien" },
    { seconds: 300, label: "5 minutes", hint: "plongée immersive" },
];

const STYLES = [
    { key: "story", label: "Histoire", subtitle: "une voix, un récit" },
    { key: "conversation", label: "Conversation", subtitle: "deux personnages" },
];

function LibraryView({ podcasts, suggestions, onGenerate, onOpen, loadingGen, quotaLine, quotaExhausted }) {
    const [duration, setDuration] = useState(120);
    const [style, setStyle] = useState("story");
    const [theme, setTheme] = useState("");
    const [freePrompt, setFreePrompt] = useState("");

    return (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12" data-testid="podcasts-page">
            <p className="text-xs tracking-[0.28em] uppercase text-inkSoft mb-3">Podcasts Lexia</p>
            <h1 className="font-serif text-3xl md:text-4xl tracking-tight">
                Un podcast à votre niveau. En cinq minutes.
            </h1>
            <p className="mt-3 text-sm text-inkSoft max-w-2xl">
                Choisissez la durée, le style et le thème. L&apos;IA écrit le script, lit l&apos;audio, et vous propose
                un quiz de compréhension, un exercice d&apos;imitation et un résumé oral à évaluer.
            </p>

            {/* Generator card */}
            <div className="mt-8 bg-white border border-lexiBorder rounded-2xl p-5 md:p-6" data-testid="podcast-generator">
                <p className="text-[11px] tracking-[0.22em] uppercase text-inkSoft mb-3">Générer un podcast</p>

                <div>
                    <p className="text-[10px] uppercase tracking-[0.22em] text-inkSoft mb-2">Durée</p>
                    <div className="flex flex-wrap gap-2">
                        {DURATIONS.map((d) => (
                            <button
                                key={d.seconds}
                                onClick={() => setDuration(d.seconds)}
                                data-testid={`gen-duration-${d.seconds}`}
                                className={`px-3 py-2 rounded-full text-xs font-medium border transition-colors ${
                                    duration === d.seconds
                                        ? "bg-ink text-white border-ink"
                                        : "bg-paperAlt text-ink border-lexiBorder hover:bg-paper"
                                }`}
                            >
                                {d.label}
                                <span className="text-[10px] text-inkSoft ml-1 normal-case">
                                    · {d.hint}
                                </span>
                            </button>
                        ))}
                    </div>
                </div>

                <div className="mt-4">
                    <p className="text-[10px] uppercase tracking-[0.22em] text-inkSoft mb-2">Style</p>
                    <div className="flex flex-wrap gap-2">
                        {STYLES.map((s) => (
                            <button
                                key={s.key}
                                onClick={() => setStyle(s.key)}
                                data-testid={`gen-style-${s.key}`}
                                className={`px-3 py-2 rounded-full text-xs font-medium border transition-colors ${
                                    style === s.key
                                        ? "bg-emerald text-white border-emerald"
                                        : "bg-paperAlt text-ink border-lexiBorder hover:bg-paper"
                                }`}
                            >
                                {s.label}
                                <span className="text-[10px] text-inkSoft ml-1 normal-case">· {s.subtitle}</span>
                            </button>
                        ))}
                    </div>
                </div>

                <div className="mt-4">
                    <label className="text-[10px] uppercase tracking-[0.22em] text-inkSoft mb-1 block">
                        Thème
                    </label>
                    <input
                        value={theme}
                        onChange={(e) => setTheme(e.target.value)}
                        placeholder="Ex : un dimanche au marché fermier"
                        data-testid="gen-theme"
                        className="w-full rounded-xl border border-lexiBorder bg-paper p-3 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta"
                    />
                </div>

                <div className="mt-3">
                    <label className="text-[10px] uppercase tracking-[0.22em] text-inkSoft mb-1 block">
                        Contexte libre <span className="normal-case">(optionnel)</span>
                    </label>
                    <input
                        value={freePrompt}
                        onChange={(e) => setFreePrompt(e.target.value)}
                        placeholder="Ex : plutôt drôle, ton chaleureux, mentionner un chien"
                        data-testid="gen-free-prompt"
                        className="w-full rounded-xl border border-lexiBorder bg-paper p-3 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta"
                    />
                </div>

                {suggestions.length > 0 && (
                    <div className="mt-4">
                        <p className="text-[10px] uppercase tracking-[0.22em] text-inkSoft mb-2">
                            Suggestions basées sur votre profil
                        </p>
                        <div className="flex flex-wrap gap-2">
                            {suggestions.slice(0, 6).map((s, i) => (
                                <button
                                    key={i}
                                    onClick={() => {
                                        setTheme(s.title_fr);
                                        setFreePrompt(s.prompt_fr || "");
                                        setStyle(s.style || "story");
                                    }}
                                    data-testid={`gen-suggestion-${i}`}
                                    className="text-[11px] px-3 py-1.5 rounded-full bg-paperAlt border border-lexiBorder hover:bg-paper text-inkSoft"
                                >
                                    {s.title_fr}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                <div className="mt-5 flex justify-end items-center gap-3">
                    {quotaLine && (
                        <span
                            className={`text-[11px] rounded-full px-3 py-1 border ${
                                quotaExhausted
                                    ? "bg-terracotta/10 text-terracotta border-terracotta/40 font-semibold"
                                    : "text-inkSoft bg-paperAlt border-lexiBorder"
                            }`}
                            data-testid="podcasts-quota"
                        >
                            {quotaLine}
                        </span>
                    )}
                    <button
                        onClick={() => onGenerate({ duration, style, theme, free_prompt: freePrompt || null })}
                        disabled={!theme.trim() || loadingGen}
                        data-testid="gen-submit"
                        className="inline-flex items-center gap-2 bg-terracotta hover:bg-terracottaDark text-white rounded-full px-5 py-2.5 text-sm font-medium disabled:opacity-60 active:scale-[0.98] transition-transform"
                    >
                        {loadingGen ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                        Générer le podcast
                    </button>
                </div>
                {loadingGen && (
                    <p className="mt-3 text-xs text-inkSoft italic">
                        Génération en cours — script + audio en une passe (≈10-20 s).
                    </p>
                )}
            </div>

            {/* Library */}
            {podcasts.length > 0 && (
                <div className="mt-10" data-testid="podcast-library">
                    <p className="text-[11px] tracking-[0.22em] uppercase text-inkSoft mb-3">Bibliothèque</p>
                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {podcasts.map((p) => (
                            <li key={p.podcast_id}>
                                <button
                                    onClick={() => onOpen(p.podcast_id)}
                                    data-testid={`podcast-library-item-${p.podcast_id}`}
                                    className="w-full text-left bg-white border border-lexiBorder rounded-2xl p-4 hover:border-terracotta hover:shadow-sm transition-all"
                                >
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="text-[10px] tracking-[0.22em] uppercase text-inkSoft">
                                            {p.duration_seconds}s · {p.style === "story" ? "Histoire" : "Conversation"} · {p.level}
                                        </span>
                                        <Headphones className="w-3.5 h-3.5 text-inkSoft" />
                                    </div>
                                    <h3 className="mt-2 font-serif text-lg leading-tight">{p.title_fr}</h3>
                                    <p className="text-xs text-inkSoft mt-1 line-clamp-2">{p.theme}</p>
                                    {p.comprehension_result && (
                                        <div className="mt-2 text-[11px] text-emerald">
                                            Compréhension : {p.comprehension_result.score}/100
                                        </div>
                                    )}
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

/* --------------------------- PLAYER VIEW --------------------------------- */

function AudioPlayer({ src, testid }) {
    const audioRef = useRef(null);
    const [playing, setPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [duration, setDuration] = useState(0);

    useEffect(() => {
        const a = audioRef.current;
        if (!a) return;
        const onTime = () => setProgress(a.currentTime);
        const onLoaded = () => setDuration(a.duration || 0);
        const onEnded = () => setPlaying(false);
        a.addEventListener("timeupdate", onTime);
        a.addEventListener("loadedmetadata", onLoaded);
        a.addEventListener("ended", onEnded);
        return () => {
            a.removeEventListener("timeupdate", onTime);
            a.removeEventListener("loadedmetadata", onLoaded);
            a.removeEventListener("ended", onEnded);
        };
    }, [src]);

    function toggle() {
        const a = audioRef.current;
        if (!a) return;
        if (playing) {
            a.pause();
            setPlaying(false);
        } else {
            a.play();
            setPlaying(true);
        }
    }
    function seek(e) {
        const a = audioRef.current;
        if (!a || !duration) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
        a.currentTime = ratio * duration;
        setProgress(a.currentTime);
    }

    const pct = duration > 0 ? (progress / duration) * 100 : 0;
    const fmt = (s) => {
        if (!Number.isFinite(s)) return "0:00";
        const m = Math.floor(s / 60);
        const sec = Math.floor(s % 60);
        return `${m}:${sec.toString().padStart(2, "0")}`;
    };
    return (
        <div className="bg-white border border-lexiBorder rounded-2xl p-4" data-testid={testid}>
            <audio ref={audioRef} src={src} preload="metadata" />
            <div className="flex items-center gap-3">
                <button
                    onClick={toggle}
                    data-testid={`${testid}-toggle`}
                    className="w-10 h-10 rounded-full bg-ink text-white hover:bg-emerald flex items-center justify-center transition-colors"
                >
                    {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
                </button>
                <div className="flex-1 min-w-0">
                    <div
                        onClick={seek}
                        className="h-1.5 bg-paperAlt rounded-full cursor-pointer overflow-hidden"
                        data-testid={`${testid}-progress`}
                    >
                        <div className="h-full bg-terracotta" style={{ width: `${pct}%` }} />
                    </div>
                    <p className="text-[10px] text-inkSoft mt-1 tabular-nums">
                        {fmt(progress)} / {fmt(duration)}
                    </p>
                </div>
            </div>
        </div>
    );
}

function PlayerView({ podcast, onBack, onRefresh }) {
    const [showTranscript, setShowTranscript] = useState(false);
    const [showFr, setShowFr] = useState(false);
    const [slowLoading, setSlowLoading] = useState(false);
    const [slowB64, setSlowB64] = useState(podcast.audio_slow_b64 || null);
    const [quizAnswers, setQuizAnswers] = useState(
        () => new Array((podcast.comprehension_questions || []).length).fill(null),
    );
    const [quizResult, setQuizResult] = useState(podcast.comprehension_result || null);
    const [summaryResult, setSummaryResult] = useState(podcast.summary_result || null);
    const [addedVocab, setAddedVocab] = useState(false);
    const [addingVocab, setAddingVocab] = useState(false);
    const [recording, setRecording] = useState(false);
    const [uploading, setUploading] = useState(false);
    const streamRef = useRef(null);
    const mrRef = useRef(null);
    const chunksRef = useRef([]);
    const { refresh: refreshDashboard } = useDashboard();

    const naturalSrc = `data:audio/mpeg;base64,${podcast.audio_natural_b64}`;
    const slowSrc = slowB64 ? `data:audio/mpeg;base64,${slowB64}` : null;

    async function loadSlow() {
        if (slowB64) return;
        setSlowLoading(true);
        try {
            const { data } = await api.post(`/podcast/${podcast.podcast_id}/audio-slow`);
            setSlowB64(data.audio_slow_b64);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSlowLoading(false);
        }
    }

    async function submitQuiz() {
        if (quizAnswers.some((a) => a === null)) {
            toast.error("Répondez à toutes les questions.");
            return;
        }
        try {
            const { data } = await api.post(`/podcast/${podcast.podcast_id}/quiz`, {
                answers: quizAnswers.map((a) => Number(a)),
            });
            setQuizResult(data.result);
            onRefresh?.();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    }

    async function addVocab() {
        setAddingVocab(true);
        try {
            const { data } = await api.post(`/podcast/${podcast.podcast_id}/add_to_reviews`, {
                words: podcast.vocabulary || [],
            });
            toast.success(`${data.added} mots ajoutés à vos révisions.`);
            setAddedVocab(true);
            refreshDashboard?.();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setAddingVocab(false);
        }
    }

    async function startRecording() {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;
            const rec = new MediaRecorder(stream);
            chunksRef.current = [];
            rec.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
            rec.onstop = async () => {
                const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
                stream.getTracks().forEach((t) => t.stop());
                setRecording(false);
                setUploading(true);
                try {
                    const form = new FormData();
                    form.append("audio", blob, "summary.webm");
                    const { data } = await api.post(`/podcast/${podcast.podcast_id}/summary`, form, {
                        headers: { "Content-Type": "multipart/form-data" },
                    });
                    setSummaryResult(data.result);
                } catch (err) {
                    toast.error(formatApiError(err));
                } finally {
                    setUploading(false);
                }
            };
            rec.start();
            mrRef.current = rec;
            setRecording(true);
        } catch {
            toast.error("Impossible d'accéder au micro.");
        }
    }
    function stopRecording() {
        if (mrRef.current && mrRef.current.state !== "inactive") mrRef.current.stop();
    }

    return (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12" data-testid="podcast-player-view">
            <button
                onClick={onBack}
                className="text-inkSoft hover:text-ink text-sm inline-flex items-center gap-1 mb-4"
                data-testid="podcast-back"
            >
                <ArrowLeft className="w-4 h-4" /> Bibliothèque
            </button>

            <p className="text-xs tracking-[0.22em] uppercase text-inkSoft">
                {podcast.duration_seconds}s · {podcast.style === "story" ? "Histoire" : "Conversation"} · {podcast.language_fr}
            </p>
            <h1 className="font-serif text-3xl md:text-4xl tracking-tight" data-testid="podcast-title">
                {podcast.title_fr}
            </h1>
            <p className="mt-1 text-sm text-inkSoft italic">{podcast.title_target}</p>

            <div className="mt-6 space-y-2">
                <AudioPlayer src={naturalSrc} testid="podcast-audio-natural" />
                <div className="flex flex-wrap items-center gap-2">
                    {slowSrc ? (
                        <div className="flex-1 min-w-[240px]">
                            <p className="text-[10px] tracking-[0.22em] uppercase text-inkSoft mb-1">
                                Version ralentie
                            </p>
                            <AudioPlayer src={slowSrc} testid="podcast-audio-slow" />
                        </div>
                    ) : (
                        <button
                            onClick={loadSlow}
                            disabled={slowLoading}
                            data-testid="podcast-load-slow"
                            className="inline-flex items-center gap-1.5 text-xs bg-ink text-white hover:bg-emerald rounded-full px-3 py-2 disabled:opacity-60"
                        >
                            {slowLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Turtle className="w-3.5 h-3.5" />}
                            Charger la version lente
                        </button>
                    )}
                </div>
            </div>

            {/* Transcript toggle */}
            <div className="mt-8">
                <div className="flex flex-wrap items-center gap-2">
                    <button
                        onClick={() => setShowTranscript((s) => !s)}
                        data-testid="podcast-toggle-transcript"
                        className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-paperAlt border border-lexiBorder hover:bg-paper"
                    >
                        <FileText className="w-3.5 h-3.5" />
                        {showTranscript ? "Masquer la transcription" : "Afficher la transcription"}
                    </button>
                    {showTranscript && (
                        <button
                            onClick={() => setShowFr((s) => !s)}
                            data-testid="podcast-toggle-fr"
                            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-paperAlt border border-lexiBorder hover:bg-paper"
                        >
                            {showFr ? "Masquer la traduction" : "Afficher la traduction française"}
                        </button>
                    )}
                </div>
                {showTranscript && (
                    <div className="mt-3 bg-white border border-lexiBorder rounded-2xl p-4 text-sm" data-testid="podcast-transcript">
                        <p className="whitespace-pre-wrap font-serif leading-relaxed">
                            {podcast.transcript_full}
                        </p>
                        {showFr && (
                            <div className="mt-3 pt-3 border-t border-lexiBorder text-inkSoft italic">
                                {podcast.transcript_fr}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Vocabulary */}
            {(podcast.vocabulary || []).length > 0 && (
                <div className="mt-8" data-testid="podcast-vocab">
                    <div className="flex items-center justify-between gap-3">
                        <p className="text-[11px] tracking-[0.22em] uppercase text-inkSoft">Vocabulaire du jour</p>
                        <button
                            onClick={addVocab}
                            disabled={addingVocab || addedVocab}
                            data-testid="podcast-add-vocab"
                            className="inline-flex items-center gap-1.5 text-xs bg-terracotta hover:bg-terracottaDark text-white rounded-full px-3 py-1.5 disabled:opacity-60"
                        >
                            {addingVocab ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                            {addedVocab ? "Ajouté" : "Tout ajouter aux révisions"}
                        </button>
                    </div>
                    <ul className="mt-3 space-y-2">
                        {podcast.vocabulary.map((v, i) => (
                            <PodcastVocabRow
                                key={i}
                                v={v}
                                index={i}
                                podcastId={podcast.podcast_id}
                                onAdded={refreshDashboard}
                                bulkAdded={addedVocab}
                            />
                        ))}
                    </ul>
                </div>
            )}

            {/* Comprehension quiz */}
            {(podcast.comprehension_questions || []).length > 0 && (
                <div className="mt-8" data-testid="podcast-quiz">
                    <p className="text-[11px] tracking-[0.22em] uppercase text-inkSoft mb-3">
                        Questions de compréhension
                    </p>
                    <ul className="space-y-4">
                        {podcast.comprehension_questions.map((q, i) => (
                            <li key={i} className="bg-white border border-lexiBorder rounded-2xl p-4">
                                <p className="font-serif" data-testid={`podcast-q-${i}`}>
                                    {i + 1}. {q.prompt_fr}
                                </p>
                                <div className="mt-2 space-y-1.5">
                                    {(q.choices_target || []).map((c, j) => {
                                        const isSelected = quizAnswers[i] === j;
                                        const graded = quizResult?.graded?.[i];
                                        const isCorrect = graded && j === Number(graded.correct_index);
                                        const isWrong = graded && isSelected && !graded.correct;
                                        return (
                                            <button
                                                key={j}
                                                onClick={() => {
                                                    if (quizResult) return;
                                                    const copy = [...quizAnswers];
                                                    copy[i] = j;
                                                    setQuizAnswers(copy);
                                                }}
                                                disabled={!!quizResult}
                                                data-testid={`podcast-q-${i}-choice-${j}`}
                                                className={`w-full text-left px-3 py-2 rounded-xl text-sm border transition-colors ${
                                                    isCorrect
                                                        ? "bg-emerald text-white border-emerald"
                                                        : isWrong
                                                          ? "bg-terracotta/10 text-terracotta border-terracotta/30"
                                                          : isSelected
                                                            ? "bg-ink text-white border-ink"
                                                            : "bg-paperAlt border-lexiBorder hover:bg-paper"
                                                }`}
                                            >
                                                {c}
                                            </button>
                                        );
                                    })}
                                </div>
                                {quizResult?.graded?.[i]?.explanation_fr && (
                                    <p className="mt-2 text-xs text-inkSoft italic">
                                        {quizResult.graded[i].explanation_fr}
                                    </p>
                                )}
                            </li>
                        ))}
                    </ul>
                    {!quizResult && (
                        <button
                            onClick={submitQuiz}
                            data-testid="podcast-quiz-submit"
                            className="mt-4 inline-flex items-center gap-2 bg-ink text-white hover:bg-emerald rounded-full px-4 py-2 text-sm transition-colors"
                        >
                            <CheckCircle2 className="w-4 h-4" /> Corriger
                        </button>
                    )}
                    {quizResult && (
                        <p
                            className="mt-4 text-sm"
                            data-testid="podcast-quiz-score"
                        >
                            Score : <span className="font-serif text-2xl text-emerald">{quizResult.score}</span>/100 ·{" "}
                            {quizResult.correct_count} / {quizResult.total} correctes.
                        </p>
                    )}
                </div>
            )}

            {/* Oral summary */}
            <div className="mt-10 bg-white border border-lexiBorder rounded-2xl p-5" data-testid="podcast-summary">
                <p className="text-[11px] tracking-[0.22em] uppercase text-inkSoft mb-2">Résumé oral</p>
                <p className="text-sm">{podcast.summary_prompt_fr}</p>

                <div className="mt-4 flex items-center gap-3">
                    {!recording ? (
                        <button
                            onClick={startRecording}
                            disabled={uploading}
                            data-testid="podcast-summary-record"
                            className="inline-flex items-center gap-2 bg-terracotta hover:bg-terracottaDark text-white rounded-full px-4 py-2 text-sm disabled:opacity-60"
                        >
                            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mic className="w-4 h-4" />}
                            S&apos;enregistrer
                        </button>
                    ) : (
                        <button
                            onClick={stopRecording}
                            data-testid="podcast-summary-stop"
                            className="inline-flex items-center gap-2 bg-ink text-white rounded-full px-4 py-2 text-sm"
                        >
                            <StopCircle className="w-4 h-4 animate-pulse" /> Arrêter
                        </button>
                    )}
                </div>

                {summaryResult && summaryResult.status === "error" && (
                    <p className="mt-4 text-sm text-terracotta" data-testid="podcast-summary-error">
                        {summaryResult.error_reason_fr}
                    </p>
                )}
                {summaryResult && summaryResult.status !== "error" && (
                    <div className="mt-4 space-y-2" data-testid="podcast-summary-result">
                        <p className="text-xs text-inkSoft">
                            Entendu : <span className="font-mono text-ink">{summaryResult.transcript}</span>
                        </p>
                        <p className="font-serif text-2xl text-emerald">
                            Score : {summaryResult.score}/100
                        </p>
                        {summaryResult.comprehension_fr && (
                            <p className="text-sm">
                                <span className="text-inkSoft text-xs">Compréhension : </span>
                                {summaryResult.comprehension_fr}
                            </p>
                        )}
                        {summaryResult.language_fr && (
                            <p className="text-sm">
                                <span className="text-inkSoft text-xs">Langue utilisée : </span>
                                {summaryResult.language_fr}
                            </p>
                        )}
                        {summaryResult.targeted_tip_fr && (
                            <p className="text-sm italic">{summaryResult.targeted_tip_fr}</p>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

/* --------------------------- MAIN PAGE ----------------------------------- */

export default function Podcasts() {
    const [podcasts, setPodcasts] = useState([]);
    const [suggestions, setSuggestions] = useState([]);
    const [openId, setOpenId] = useState(null);
    const [openPodcast, setOpenPodcast] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingGen, setLoadingGen] = useState(false);
    const navigate = useNavigate();
    const { data: quotas, refetch: refetchQuotas } = useQuotas();
    const quotaLine = formatQuotaLine(quotas?.features?.podcast);
    const quotaExhausted = isQuotaExhausted(quotas?.features?.podcast);

    async function loadLibrary() {
        try {
            const [lib, sug] = await Promise.all([
                api.get("/podcast/library"),
                api.get("/podcast/suggestions"),
            ]);
            setPodcasts(lib.data.podcasts || []);
            setSuggestions(sug.data.suggestions || []);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => {
        loadLibrary();
        // eslint-disable-next-line react-hooks/exhaustive-deps
        // Intentional mount-only fetch — `loadLibrary` is a stable closure
        // over local state setters; adding it to deps would require useCallback
        // (which pulls in all state setters) for no observable benefit and
        // would risk re-fetching the library on every state update.
    }, []);

    async function generate(payload) {
        setLoadingGen(true);
        try {
            const { data } = await api.post("/podcast/generate", payload);
            setOpenPodcast(data.podcast);
            setOpenId(data.podcast.podcast_id);
            loadLibrary();
            refetchQuotas();
        } catch (err) {
            if (!handlePaywallError(err)) toast.error(formatApiError(err));
            refetchQuotas();
        } finally {
            setLoadingGen(false);
        }
    }

    async function openOne(id) {
        try {
            const { data } = await api.get(`/podcast/${id}`);
            setOpenPodcast(data.podcast);
            setOpenId(id);
        } catch (err) {
            toast.error(formatApiError(err));
        }
    }

    if (loading) {
        return (
            <div className="min-h-[60vh] flex flex-col items-center justify-center">
                <Loader2 className="w-6 h-6 text-terracotta animate-spin" />
            </div>
        );
    }

    if (openPodcast) {
        return (
            <PlayerView
                podcast={openPodcast}
                onBack={() => {
                    setOpenPodcast(null);
                    setOpenId(null);
                }}
                onRefresh={loadLibrary}
            />
        );
    }

    return (
        <LibraryView
            podcasts={podcasts}
            suggestions={suggestions}
            onGenerate={generate}
            onOpen={openOne}
            loadingGen={loadingGen}
            quotaLine={quotaLine}
            quotaExhausted={quotaExhausted}
        />
    );
}
